/**
 * Where the person was trying to go before the Hub asked them to sign in.
 *
 * Kyu, 3 September: "I need to be able to have the ability to send a
 * shareable link (to any TAP Hub members) of a project or a task link."
 *
 * The link format already existed — the inbox has been building
 * /projects/<id>?task=<id>&comment=<id> since Stage 11 — but a link is only
 * shareable if it survives the sign-in gate, and it did not. RequireTap sent
 * a signed-out visitor to /login carrying `state={{ from: pathname }}`:
 * the SEARCH was dropped, so ?task= was already gone, and PublicOnlyRoute
 * never read that state at all — it sent every approved account to its home.
 * So the recipient of a shared link signed in and landed on Home, with
 * nothing on the screen explaining why they were not looking at the task.
 *
 * Router state cannot carry it either way, because signing in leaves the
 * application entirely: Google's consent screen is a full page load, and the
 * router — with its state — is destroyed and rebuilt. sessionStorage is the
 * only thing on this path that survives that round trip and still dies with
 * the tab.
 *
 * ── The three rules this file exists to enforce ────────────────────────────
 *
 * 1. Only ever redirect INSIDE the Hub. A stored value is a redirect target,
 *    and a redirect target taken from a URL is how open-redirect phishing
 *    works: //evil.example and https://evil.example are both valid values of
 *    `location.pathname + location.search` if an attacker can arrange the
 *    URL. Anything not starting with a single '/' is refused on the way in
 *    AND on the way out, because a value written by an older version of this
 *    code is not trustworthy either.
 *
 * 2. Never bounce someone to /login or /auth/callback. Storing the login page
 *    as the destination makes signing in a loop.
 *
 * 3. Forget it quickly. Ten minutes. Without an expiry, a link opened and
 *    abandoned on Monday teleports you away from Home on Thursday, which
 *    reads as the Hub being possessed rather than as a feature.
 *
 * Storage is best-effort throughout: Safari in private mode throws on
 * sessionStorage access rather than returning null, and a sign-in that
 * cannot remember where you were going must still be a sign-in that works.
 */

const KEY = 'tap.intendedDestination';

/** Ten minutes. Long enough to finish Google's sign-in, short enough to forget. */
const MAX_AGE_MS = 10 * 60 * 1000;

interface Stored {
  to: string;
  at: number;
}

/**
 * Whether a stored value may be navigated to.
 *
 * Exported because this is the security-relevant half of the file and it is
 * worth pinning directly rather than through a component.
 */
export function isSafeDestination(value: unknown): value is string {
  if (typeof value !== 'string' || value === '') return false;
  // Must be an absolute path within this origin. A leading '//' is a
  // protocol-relative URL to another host, which the browser will happily
  // follow.
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  // A backslash is treated as a slash by some browsers when parsing a URL,
  // so '/\evil.example' is not the internal path it looks like.
  if (value.includes('\\')) return false;
  // The auth pages are not destinations; storing one makes a loop.
  if (value === '/login' || value.startsWith('/login?')) return false;
  if (value === '/auth' || value.startsWith('/auth/')) return false;
  return true;
}

/**
 * Remember where someone was headed. Silently does nothing for a destination
 * that fails the rules above, and for storage that refuses to be written.
 */
export function rememberDestination(to: string, now: number = Date.now()): void {
  if (!isSafeDestination(to)) return;
  try {
    const stored: Stored = { to, at: now };
    window.sessionStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    // Private browsing, a storage quota, a browser configured to refuse it.
    // Sign-in still works; it just lands on Home.
  }
}

/**
 * Read the destination and forget it in the same breath.
 *
 * Taking rather than peeking is deliberate: the value is consumed by a
 * redirect, and a value left behind would fire again on the next sign-in in
 * the same tab.
 */
export function takeDestination(now: number = Date.now()): string | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(KEY);
    window.sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const { to, at } = parsed as Partial<Stored>;
  if (!isSafeDestination(to)) return null;
  if (typeof at !== 'number' || !Number.isFinite(at)) return null;
  // Also refuses a timestamp from the future, which means a clock change
  // rather than a real intent.
  if (at > now || now - at > MAX_AGE_MS) return null;
  return to;
}

/** Drop anything remembered. Used when signing out. */
export function forgetDestination(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do about it and nothing depends on it.
  }
}
